/**
 * SEO metadata shared by the funnel runtime and the admin UI (NX-11852).
 *
 * Values are configured per application (`applications.seo_json`) and per
 * funnel (`funnels.seo_json`, snapshotted into `funnel_versions`). The funnel
 * value wins; the application value is the brand default. Both support
 * `{token|fallback}` placeholders resolved against `SeoTokens`.
 */
import { interpolate } from "@funnel-ui/interpolate";

export type SeoConfig = {
  page_title?: string;
  meta_description?: string;
};

/** Values available to `{token|fallback}` placeholders on page load. */
export type SeoTokens = {
  brand_name: string;
  funnel_name?: string;
  inlet_slug?: string;
};

/** Token names offered in the admin UI, in the order they're documented. */
export const SEO_TOKEN_NAMES = ["brand_name", "funnel_name", "inlet_slug"] as const;

export type ResolvedSeoMetadata = {
  title: string;
  description: string | null;
};

const readTemplate = (
  config: SeoConfig | null | undefined,
  key: keyof SeoConfig,
): string | null => {
  const value = config?.[key];
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
};

/** Narrows an untrusted value (API payload, parsed JSON column) to `SeoConfig`. */
export const coerceSeoConfig = (value: unknown): SeoConfig => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return {};
  const { page_title, meta_description } = value as Record<string, unknown>;
  return {
    ...(typeof page_title === "string" ? { page_title } : {}),
    ...(typeof meta_description === "string" ? { meta_description } : {}),
  };
};

/** Parses a `seo_json` blob, tolerating malformed or non-object values. */
export const parseSeoConfig = (raw: string | null | undefined): SeoConfig => {
  if (typeof raw !== "string" || raw.trim().length === 0) return {};
  try {
    return coerceSeoConfig(JSON.parse(raw));
  } catch {
    return {};
  }
};

/**
 * Funnel config overrides the application default; the brand name is the last
 * resort for the title so a document is never left without one.
 */
export const resolveSeoMetadata = (params: {
  funnel?: SeoConfig | null;
  application?: SeoConfig | null;
  tokens: SeoTokens;
}): ResolvedSeoMetadata => {
  const { funnel, application, tokens } = params;
  const values: Record<string, string> = {
    brand_name: tokens.brand_name,
    ...(tokens.funnel_name !== undefined ? { funnel_name: tokens.funnel_name } : {}),
    ...(tokens.inlet_slug !== undefined ? { inlet_slug: tokens.inlet_slug } : {}),
  };

  const titleTemplate =
    readTemplate(funnel, "page_title") ?? readTemplate(application, "page_title");
  const descriptionTemplate =
    readTemplate(funnel, "meta_description") ?? readTemplate(application, "meta_description");

  const title = titleTemplate === null ? tokens.brand_name : interpolate(titleTemplate, values);
  const description =
    descriptionTemplate === null ? null : interpolate(descriptionTemplate, values);

  return {
    title: title.trim().length > 0 ? title.trim() : tokens.brand_name,
    description: description !== null && description.trim().length > 0 ? description.trim() : null,
  };
};
